/**
 * CheckOutReminder — Feb 2026 · Phase 84.27
 *
 * Companion nudge to CheckInReminder. Fires after the user has been
 * checked in for **8 hours + 15 minutes of buffer** to remind them to
 * actually click "Check out" on the dashboard.
 *
 * Rules
 *   • IST only — gated on `Intl.DateTimeFormat().resolvedOptions().timeZone`.
 *     A traveler on EST/PST sees no nudge.
 *   • Internal users only (skips role="client"/"vendor").
 *   • Silent on weekends + holidays (same gate as CheckInReminder).
 *   • Fires the first ping as soon as (check_in + 8h15m) elapses, then
 *     every 15 min thereafter — until the user checks out.
 *   • Toast + beep + tab-title flash. No auto-checkout button (same
 *     deliberate friction the check-in reminder has since Phase 82).
 */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CHECKOUT_AFTER_MS = (8 * 60 + 15) * 60 * 1000;  // 8h 15m buffer
const REPEAT_MS = 15 * 60 * 1000;                     // Nudge every 15 min once elapsed
const POLL_MS = 60 * 1000;                            // Poll attendance/today every minute
const IST_ZONE = "Asia/Kolkata";

function safeBeep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    // Two-tone descending — distinct from the check-in reminder's single 880Hz square.
    o.frequency.value = 660;
    o.type = "sine";
    o.connect(g); g.connect(ctx.destination);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.55);
    o.start();
    o.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.5);
    o.stop(ctx.currentTime + 0.6);
  } catch (err) {
    logger.warn("Checkout reminder beep blocked:", err?.message || err);
  }
}

/** Parse the IST-naive ISO string the backend stores in `check_in`.
 *  now_ist_naive_iso() emits "2026-02-07T18:23:45" — no tz marker.
 *  We interpret it as an IST wall clock so `elapsed` is correct for
 *  any browser tz. */
function istIsoToUtcDate(iso) {
  if (!iso) return null;
  // Treat the string as IST (UTC+5:30). Build a UTC Date accordingly.
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(iso);
  if (!m) return new Date(iso);
  const [, y, mo, d, h, mi, s] = m;
  return new Date(Date.UTC(+y, +mo - 1, +d, +h - 5, +mi - 30, +s));
}

export default function CheckOutReminder() {
  const { user } = useAuth();
  const titleRef = useRef(document.title);
  const lastFiredRef = useRef(0);
  const [holidaySet, setHolidaySet] = useState(null);

  // Hydrate holiday set (share cache logic with CheckInReminder — both call
  // the same endpoint; browser cache dedupes at the HTTP layer).
  useEffect(() => {
    if (!user || user === false || user.role === "client" || user.role === "vendor") return;
    let cancelled = false;
    api.get("/leaves/holidays", { params: { year: new Date().getFullYear() } })
      .then(({ data }) => { if (!cancelled) setHolidaySet(new Set((data || []).map((h) => h.date))); })
      .catch((err) => {
        if (!cancelled) {
          logger.warn("Checkout reminder · holiday fetch failed:", err?.message || err);
          setHolidaySet(new Set());
        }
      });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!user || user === false || user.role === "client" || user.role === "vendor") return;
    if (holidaySet === null) return;
    // IST-only per product spec — skip travelers.
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && tz !== IST_ZONE) return;
    } catch { /* older browsers — fall through */ }

    let cancelled = false;

    function isOffDay(now) {
      const dow = now.getDay();
      if (dow === 0 || dow === 6) return true;
      const isoLocal = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      return holidaySet.has(isoLocal);
    }

    async function ping() {
      if (cancelled) return;
      const now = new Date();
      if (isOffDay(now)) return;
      try {
        const { data } = await api.get("/attendance/today");
        if (!data || !data.check_in) return;        // Not checked in yet
        if (data.check_out) return;                  // Already checked out
        const inAt = istIsoToUtcDate(data.check_in);
        if (!inAt) return;
        const elapsed = Date.now() - inAt.getTime();
        if (elapsed < CHECKOUT_AFTER_MS) return;

        // Throttle: don't fire more often than REPEAT_MS between pings.
        if (Date.now() - lastFiredRef.current < REPEAT_MS) return;
        lastFiredRef.current = Date.now();

        const hours = Math.floor(elapsed / 3_600_000);
        const mins = Math.round((elapsed % 3_600_000) / 60_000);

        safeBeep();
        toast.warning(`🕒 You've been checked in for ${hours}h ${mins}m — time to check out on Home.`, {
          duration: 8000,
        });
        // Tab title flash to catch attention if the toast is missed.
        let on = false;
        const flash = setInterval(() => {
          document.title = on ? titleRef.current : "🕒 Check out!";
          on = !on;
        }, 800);
        setTimeout(() => { clearInterval(flash); document.title = titleRef.current; }, 4800);
      } catch (err) {
        logger.warn("Checkout reminder poll failed:", err?.message || err);
      }
    }

    // First check after 8s to let the app settle, then poll every minute.
    const initial = setTimeout(ping, 8000);
    const id = setInterval(ping, POLL_MS);
    return () => { cancelled = true; clearTimeout(initial); clearInterval(id); };
  }, [user, holidaySet]);

  return null;
}
